import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  TextField,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { format } from 'date-fns';

import { ApplicationState } from '../../../store';
import { fetchPaymentsRequest } from '../../../store/modules/admin/actions';
import { Container, FiltersRow, useStyles } from './PaymentsStyles';
import api from '../../../services/api';
import { toast } from 'react-toastify';

export default function PaymentsView() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { payments, loading } = useSelector((state: ApplicationState) => state.admin);

  const [statusFilter, setStatusFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');

  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editingPayment, setEditingPayment] = useState<any>(null);

  const [createForm, setCreateForm] = useState({
    userEmail: '',
    amount: '',
    referenceMonth: format(new Date(), 'yyyy-MM'),
    status: 'paid',
    notes: '',
  });

  const [editForm, setEditForm] = useState({
    status: '',
    amount: '',
    notes: '',
  });

  useEffect(() => {
    loadPayments();
  }, [statusFilter, monthFilter, sourceFilter]); // eslint-disable-line

  function loadPayments() {
    const filters: any = {};
    if (statusFilter) filters.status = statusFilter;
    if (monthFilter) filters.month = monthFilter;
    if (sourceFilter) filters.source = sourceFilter;
    dispatch(fetchPaymentsRequest(filters));
  }

  async function handleCreate() {
    try {
      // First find user by email to get userId
      const usersRes = await api.get('/admin/users');
      const user = usersRes.data.find(
        (u: any) => u.email.toLowerCase() === createForm.userEmail.toLowerCase().trim()
      );
      if (!user) {
        toast.error('Usuario nao encontrado com este email.');
        return;
      }

      await api.post('/admin/payments', {
        userId: user._id,
        amount: parseFloat(createForm.amount) || 0,
        referenceMonth: createForm.referenceMonth,
        status: createForm.status,
        notes: createForm.notes,
      });
      toast.success('Pagamento criado!');
      setCreateDialog(false);
      setCreateForm({ userEmail: '', amount: '', referenceMonth: format(new Date(), 'yyyy-MM'), status: 'paid', notes: '' });
      loadPayments();
    } catch (err) {
      const msg = (err.response && err.response.data && err.response.data.error) || 'Erro ao criar pagamento.';
      toast.error(msg);
    }
  }

  function openEdit(payment: any) {
    setEditingPayment(payment);
    setEditForm({
      status: payment.status,
      amount: String(payment.amount),
      notes: payment.notes || '',
    });
    setEditDialog(true);
  }

  async function handleEdit() {
    try {
      await api.put(`/admin/payments/${editingPayment._id}`, {
        status: editForm.status,
        amount: parseFloat(editForm.amount) || 0,
        notes: editForm.notes,
      });
      toast.success('Pagamento atualizado!');
      setEditDialog(false);
      loadPayments();
    } catch {
      toast.error('Erro ao atualizar pagamento.');
    }
  }

  async function handleDelete(paymentId: string) {
    if (!window.confirm('Deseja realmente remover este pagamento?')) return;
    try {
      await api.delete(`/admin/payments/${paymentId}`);
      toast.success('Pagamento removido!');
      loadPayments();
    } catch {
      toast.error('Erro ao remover pagamento.');
    }
  }

  function getStatusChip(status: string) {
    if (status === 'paid') return <Chip label="Pago" size="small" className={classes.chipPaid} />;
    if (status === 'pending') return <Chip label="Pendente" size="small" className={classes.chipPending} />;
    return <Chip label="Atrasado" size="small" className={classes.chipOverdue} />;
  }

  function getSourceChip(source: string) {
    if (source === 'kiwify') return <Chip label="Kiwify" size="small" className={classes.chipKiwify} />;
    return <Chip label="Manual" size="small" className={classes.chipManual} />;
  }

  if (loading && payments.length === 0) {
    return <CircularProgress style={{ margin: '40px auto', display: 'block' }} />;
  }

  return (
    <Container>
      <FiltersRow>
        <FormControl variant="outlined" size="small" className={classes.filterField}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as string)}

          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="paid">Pago</MenuItem>
            <MenuItem value="pending">Pendente</MenuItem>
            <MenuItem value="overdue">Atrasado</MenuItem>
          </Select>
        </FormControl>
        <TextField
          className={classes.filterField}
          variant="outlined"
          size="small"
          label="Mes (yyyy-MM)"
          value={monthFilter}
          onChange={e => setMonthFilter(e.target.value)}
        />
        <FormControl variant="outlined" size="small" className={classes.filterField}>
          <InputLabel>Origem</InputLabel>
          <Select
            value={sourceFilter}
            onChange={e => setSourceFilter(e.target.value as string)}

          >
            <MenuItem value="">Todas</MenuItem>
            <MenuItem value="kiwify">Kiwify</MenuItem>
            <MenuItem value="manual">Manual</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" color="primary" onClick={() => setCreateDialog(true)}>
          Novo Pagamento
        </Button>
      </FiltersRow>

      <div className={classes.tableContainer}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Usuario</TableCell>
              <TableCell>Valor</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Mes Ref.</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Origem</TableCell>
              <TableCell>Notas</TableCell>
              <TableCell>Acoes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map(payment => (
              <TableRow key={payment._id}>
                <TableCell>
                  {payment.userName}
                  <br />
                  <span style={{ fontSize: '11px', opacity: 0.7 }}>{payment.userEmail}</span>
                </TableCell>
                <TableCell>R$ {payment.amount.toFixed(2)}</TableCell>
                <TableCell>{format(new Date(payment.date), 'dd/MM/yyyy')}</TableCell>
                <TableCell>{payment.referenceMonth}</TableCell>
                <TableCell>{getStatusChip(payment.status)}</TableCell>
                <TableCell>{getSourceChip(payment.source)}</TableCell>
                <TableCell>{payment.notes || '-'}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => openEdit(payment)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  {payment.source === 'manual' && (
                    <IconButton size="small" onClick={() => handleDelete(payment._id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {payments.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Nenhum pagamento encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create Payment Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)}>
        <DialogTitle>Novo Pagamento</DialogTitle>
        <DialogContent>
          <div className={classes.dialogContent}>
            <TextField
              label="Email do usuario"
              variant="outlined"
              size="small"
              value={createForm.userEmail}
              onChange={e => setCreateForm({ ...createForm, userEmail: e.target.value })}
            />
            <TextField
              label="Valor"
              variant="outlined"
              size="small"
              type="number"
              value={createForm.amount}
              onChange={e => setCreateForm({ ...createForm, amount: e.target.value })}
            />
            <TextField
              label="Mes Referencia (yyyy-MM)"
              variant="outlined"
              size="small"
              value={createForm.referenceMonth}
              onChange={e => setCreateForm({ ...createForm, referenceMonth: e.target.value })}
            />
            <FormControl variant="outlined" size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={createForm.status}
                onChange={e => setCreateForm({ ...createForm, status: e.target.value as string })}
    
              >
                <MenuItem value="paid">Pago</MenuItem>
                <MenuItem value="pending">Pendente</MenuItem>
                <MenuItem value="overdue">Atrasado</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Notas"
              variant="outlined"
              size="small"
              multiline
              rows={2}
              value={createForm.notes}
              onChange={e => setCreateForm({ ...createForm, notes: e.target.value })}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={handleCreate}>
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Payment Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)}>
        <DialogTitle>Editar Pagamento</DialogTitle>
        <DialogContent>
          <div className={classes.dialogContent}>
            <TextField
              label="Valor"
              variant="outlined"
              size="small"
              type="number"
              value={editForm.amount}
              onChange={e => setEditForm({ ...editForm, amount: e.target.value })}
            />
            <FormControl variant="outlined" size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={editForm.status}
                onChange={e => setEditForm({ ...editForm, status: e.target.value as string })}
    
              >
                <MenuItem value="paid">Pago</MenuItem>
                <MenuItem value="pending">Pendente</MenuItem>
                <MenuItem value="overdue">Atrasado</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Notas"
              variant="outlined"
              size="small"
              multiline
              rows={2}
              value={editForm.notes}
              onChange={e => setEditForm({ ...editForm, notes: e.target.value })}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={handleEdit}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
